import { getUserById } from "../lib/mockData";
import { MapPin, Monitor, Bookmark } from "lucide-react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { CATEGORY_COLORS } from "../lib/skills";
import { useT } from "../lib/useLanguage";

const TYPE_BADGE = {
  offer: { label: "Offre", bg: "#B9F85A", color: "#101828" },
  request: { label: "Besoin", bg: "#FF6B4A", color: "#fff" },
  session: { label: "Session", bg: "#A7D8FF", color: "#101828" },
};

export default function SwipeCard({ card, onAction, style }) {
  const t = useT();
  const user = getUserById(card.userId);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 220], [-10, 10]);
  const skipOpacity = useTransform(x, [-100, -30, 0], [1, 0.3, 0]);
  const msgOpacity = useTransform(x, [0, 30, 100], [0, 0.3, 1]);

  const colors = CATEGORY_COLORS[card.category] || CATEGORY_COLORS["Digital"];
  const badge = TYPE_BADGE[card.type] || TYPE_BADGE.offer;

  function handleDragEnd(_, info) {
    if (info.offset.x > 100) onAction("positive");
    else if (info.offset.x < -100) onAction("skip");
  }

  return (
    <motion.div
      style={{ x, rotate, ...style }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.65}
      onDragEnd={handleDragEnd}
      className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none select-none"
    >
      {/* Skip / Swap overlays */}
      <motion.div style={{ opacity: skipOpacity }}
        className="absolute top-5 left-5 z-20 px-3 py-1.5 rounded-xl border-2 border-red-400 text-red-400 text-xs font-extrabold uppercase tracking-wider rotate-[-15deg]">
        Skip
      </motion.div>
      <motion.div style={{ opacity: msgOpacity }}
        className="absolute top-5 right-5 z-20 px-3 py-1.5 rounded-xl border-2 text-xs font-extrabold uppercase tracking-wider rotate-[15deg]"
        style={{ borderColor: "#FF6B4A", color: "#FF6B4A" }}>
        Swap
      </motion.div>

      <div className="bg-white rounded-3xl border border-border shadow-lg h-full flex flex-col overflow-hidden">
        {/* Color band */}
        <div className="h-2 w-full" style={{ backgroundColor: colors.accent }} />

        <div className="p-5 flex flex-col flex-1">
          {/* Top badges row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ backgroundColor: badge.bg, color: badge.color }}>
                {badge.label}
              </span>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}>
                {card.category}
              </span>
            </div>
            <button onClick={e => { e.stopPropagation(); onAction("save"); }}
              className="p-1.5 rounded-full hover:bg-muted transition">
              <Bookmark className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Title — big and dominant */}
          <h2 className="text-xl font-extrabold leading-tight mb-2" style={{ color: "#101828" }}>
            {card.title}
          </h2>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-2">
            {card.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {card.tags?.slice(0, 4).map(tag => (
              <span key={tag} className="text-xs px-2.5 py-1 rounded-full font-semibold bg-muted text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>

          {/* Profile row */}
          {user && (
            <div className="flex items-center gap-2.5 py-3 border-t border-b border-border mb-3">
              <img src={user.photo} alt="" className="h-9 w-9 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{user.firstName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.ageRange} · {user.status}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{card.duration}</span>
            </div>
          )}

          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{card.zone}</span>
            <span className="flex items-center gap-1"><Monitor className="h-3 w-3" />{card.format}</span>
          </div>

          {/* Match reason */}
          {card.matchReason && (
            <div className="px-3 py-2 rounded-xl text-xs font-semibold" style={{ backgroundColor: "#B9F85A25", color: "#3d6b1f" }}>
              ✦ {card.matchReason}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />
        </div>

        {/* BIG action buttons at bottom */}
        <div className="flex gap-2 px-4 pb-5">
          <button
            onClick={e => { e.stopPropagation(); onAction("skip"); }}
            className="flex-1 py-4 rounded-2xl text-sm font-bold border-2 border-border text-muted-foreground hover:border-red-300 hover:text-red-400 transition-all active:scale-95"
            style={{ backgroundColor: "#f9f6f2" }}>
            {t("card_skip")}
          </button>
          <button
            onClick={e => { e.stopPropagation(); onAction("positive"); }}
            className="flex-[2] py-4 rounded-2xl text-sm font-extrabold text-white transition-all active:scale-95"
            style={{ backgroundColor: "#FF6B4A" }}>
            {t("card_ask")} ↔
          </button>
        </div>
      </div>
    </motion.div>
  );
}